import React, { useEffect, useState, useRef } from 'react';
import { StatsBlock } from './AnimatedCounter.jsx';

// Parallax Layer Component
function ParallaxLayer({ src, speed = 0.5, className = '', ...props }) {
  const [offset, setOffset] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset * speed);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);
  
  return (
    <div 
      className={`absolute inset-0 ${className}`}
      style={{ transform: `translateY(${offset}px)` }}
      {...props}
    >
      {src && (
        <img 
          src={src} 
          alt="" 
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
    </div>
  );
}

// Glass Morphism Panel Component
function GlassMorphismPanel({ children, className = '', ...props }) {
  return (
    <div 
      className={`
        glass-morphism relative
        backdrop-blur-md bg-white/20
        border border-white/30
        shadow-2xl
        ${className}
      `}
      {...props}
    >
      {/* Animated edge refraction */}
      <div className="absolute inset-0 rounded-inherit opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent 
                        animate-[sweep_8s_ease-in-out_infinite] rounded-inherit" />
      </div>
      
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

export function HeroSection({
  headline,
  subheadline,
  actions,
  stats = [],
  parallaxLayers = [],
  background = '',
  children,
  className = '',
  style = {},
  enableParallax = true,
  showStats = true,
  ...props
}) {
  const heroRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    // Trigger entrance animation after mount
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <section
      ref={heroRef}
      className={`
        relative w-full min-h-screen 
        flex flex-col items-center justify-center 
        bg-background overflow-hidden
        ${className}
      `}
      style={style}
      {...props}
    >
      {/* Multi-Layer Parallax Background */}
      {enableParallax && parallaxLayers.length > 0 ? (
        <>
          {parallaxLayers.map((layer, index) => (
            <ParallaxLayer
              key={index}
              src={layer.src}
              speed={layer.speed || (index + 1) * 0.3}
              className={layer.className || ''}
            />
          ))}
        </>
      ) : background ? (
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${background})` }}
        />
      ) : null}
      
      {/* Default Parallax Layers if none provided */}
      {enableParallax && parallaxLayers.length === 0 && (
        <>
          {/* Background Layer - Oven Glow */}
          <ParallaxLayer 
            speed={0.2}
            className="bg-gradient-radial from-oven-glow/20 via-transparent to-transparent"
          />
          
          {/* Midground - Bakery Interior */}
          <ParallaxLayer 
            speed={0.4}
            className="opacity-30"
          />
          
          {/* Foreground - Flour Particles */}
          <ParallaxLayer 
            speed={0.8}
            className="pointer-events-none"
          >
            <div className="flour-particles w-full h-full" />
          </ParallaxLayer>
        </>
      )}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background/95 pointer-events-none" />
      
      {/* Main Content in Glass Morphism Panel */}
      <div className="relative z-20 flex flex-col items-center justify-center text-center px-4 py-16 max-w-5xl mx-auto">
        <GlassMorphismPanel 
          className={`
            p-8 md:p-12 lg:p-16 max-w-4xl mx-auto
            transform transition-all duration-1000 ease-out-crust
            ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
          `}
        >
          {/* Headline with Hand-Lettered Style */}
          {headline && (
            <h1 className={`
              font-heading font-extrabold mb-6
              text-5xl md:text-7xl lg:text-8xl
              text-primary drop-shadow-2xl
              bg-gradient-to-b from-primary to-primary/80 bg-clip-text
              transform transition-all duration-1000 ease-spring
              ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ 
              letterSpacing: '-0.02em',
              animationDelay: '200ms',
              textShadow: '0 4px 8px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.6)'
            }}>
              {headline}
            </h1>
          )}
          
          {/* Subheadline */}
          {subheadline && (
            <p className={`
              text-xl md:text-2xl lg:text-3xl 
              text-text-secondary mb-8 font-body
              max-w-3xl mx-auto leading-relaxed
              transform transition-all duration-1000 ease-out-crust
              ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ animationDelay: '400ms' }}>
              {subheadline}
            </p>
          )}
          
          {/* Action Buttons */}
          {actions && (
            <div className={`
              flex flex-wrap gap-4 justify-center mb-8
              transform transition-all duration-1000 ease-out-crust
              ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ animationDelay: '600ms' }}>
              {actions}
            </div>
          )}
          
          {/* Custom Children */}
          {children && (
            <div className={`
              transform transition-all duration-1000 ease-out-crust
              ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ animationDelay: '800ms' }}>
              {children}
            </div>
          )}
        </GlassMorphismPanel>
        
        {/* Statistics Block */}
        {showStats && stats.length > 0 && (
          <div className={`
            mt-12 w-full max-w-4xl mx-auto
            transform transition-all duration-1000 ease-out-crust
            ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
          `}
          style={{ animationDelay: '1000ms' }}>
            <GlassMorphismPanel className="p-8">
              <StatsBlock stats={stats} />
            </GlassMorphismPanel>
          </div>
        )}
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-text-muted animate-bounce">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
}

export default HeroSection;
